import { openDB, DBSchema, IDBPDatabase } from 'idb';

// Reference to a synced photo (bytes live in GCS, served via /api/photos/:id/*).
export interface PhotoUrlRef {
  id: string;
  thumbUrl: string;
  originalUrl: string;
  fileName?: string | null;
  mimeType?: string | null;
}

// Define the database schema
export interface TrappDB extends DBSchema {
  snares: {
    key: string;
    value: {
      id: string;
      snareNumber: string;
      groupId: string;
      userId: string;
      latitude: number;
      longitude: number;
      material: string;
      description: string;
      photos: string[]; // Base64 encoded images (local, pre-sync)
      photoUrls?: PhotoUrlRef[]; // Server photo references (post-sync)
      createdAt: string;
      updatedAt: string;
      syncStatus: 'pending' | 'synced' | 'failed';
    };
    indexes: {
      'by-sync-status': 'syncStatus';
      'by-created-at': 'createdAt';
    };
  };

  carcasses: {
    key: string;
    value: {
      id: string;
      carcassNumber: string;
      groupId: string;
      userId: string;
      latitude: number;
      longitude: number;
      species: string;
      hadSnare: boolean;
      description: string;
      photos: string[]; // Base64 encoded images (local, pre-sync)
      photoUrls?: PhotoUrlRef[]; // Server photo references (post-sync)
      createdAt: string;
      updatedAt: string;
      syncStatus: 'pending' | 'synced' | 'failed';
    };
    indexes: {
      'by-sync-status': 'syncStatus';
      'by-created-at': 'createdAt';
    };
  };

  syncQueue: {
    key: string;
    value: {
      id: string;
      operation: 'create' | 'update' | 'delete';
      resource: 'snare' | 'carcass';
      resourceId: string;
      data: unknown;
      createdAt: string;
      retryCount: number;
      lastError?: string;
    };
    indexes: {
      'by-created-at': 'createdAt';
    };
  };

  settings: {
    key: string;
    value: {
      key: string;
      value: unknown;
      updatedAt: string;
    };
  };
}

const DB_NAME = 'trapp-db';
const DB_VERSION = 1;

let dbInstance: IDBPDatabase<TrappDB> | null = null;

export async function getDB(): Promise<IDBPDatabase<TrappDB>> {
  if (dbInstance) {
    return dbInstance;
  }

  dbInstance = await openDB<TrappDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Create snares store
      if (!db.objectStoreNames.contains('snares')) {
        const snareStore = db.createObjectStore('snares', { keyPath: 'id' });
        snareStore.createIndex('by-sync-status', 'syncStatus');
        snareStore.createIndex('by-created-at', 'createdAt');
      }

      // Create carcasses store
      if (!db.objectStoreNames.contains('carcasses')) {
        const carcassStore = db.createObjectStore('carcasses', { keyPath: 'id' });
        carcassStore.createIndex('by-sync-status', 'syncStatus');
        carcassStore.createIndex('by-created-at', 'createdAt');
      }

      // Create sync queue store
      if (!db.objectStoreNames.contains('syncQueue')) {
        const syncQueueStore = db.createObjectStore('syncQueue', { keyPath: 'id' });
        syncQueueStore.createIndex('by-created-at', 'createdAt');
      }

      // Create settings store
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings', { keyPath: 'key' });
      }
    },
  });

  return dbInstance;
}

// Snare operations
export async function addSnare(snare: TrappDB['snares']['value']): Promise<void> {
  const db = await getDB();
  await db.put('snares', snare);
}

export async function getSnares(): Promise<TrappDB['snares']['value'][]> {
  const db = await getDB();
  return db.getAll('snares');
}

export async function getSnaresBySyncStatus(status: 'pending' | 'synced' | 'failed'): Promise<TrappDB['snares']['value'][]> {
  const db = await getDB();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return db.getAllFromIndex('snares', 'by-sync-status', status as any);
}

export async function updateSnareSyncStatus(id: string, status: 'pending' | 'synced' | 'failed'): Promise<void> {
  const db = await getDB();
  const snare = await db.get('snares', id);
  if (snare) {
    snare.syncStatus = status;
    snare.updatedAt = new Date().toISOString();
    await db.put('snares', snare);
  }
}

/**
 * Reconcile a local snare with the server's canonical record after a successful
 * sync: adopt the server id, snareNumber and photo URLs, mark synced. If the
 * server assigned a different id, the old local record is removed atomically.
 */
export async function reconcileSnare(
  localId: string,
  server: { id: string; snareNumber?: string; photos?: PhotoUrlRef[] | null }
): Promise<void> {
  const db = await getDB();
  const existing = await db.get('snares', localId);
  if (!existing) return;

  const reconciled = {
    ...existing,
    id: server.id || existing.id,
    snareNumber: server.snareNumber || existing.snareNumber,
    photoUrls: server.photos ?? existing.photoUrls,
    syncStatus: 'synced' as const,
    updatedAt: new Date().toISOString(),
  };

  const tx = db.transaction('snares', 'readwrite');
  if (reconciled.id !== localId) {
    await tx.store.delete(localId);
  }
  await tx.store.put(reconciled);
  await tx.done;
}

export async function deleteSnare(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('snares', id);
}

// Carcass operations
export async function addCarcass(carcass: TrappDB['carcasses']['value']): Promise<void> {
  const db = await getDB();
  await db.put('carcasses', carcass);
}

export async function getCarcasses(): Promise<TrappDB['carcasses']['value'][]> {
  const db = await getDB();
  return db.getAll('carcasses');
}

export async function getCarcassesBySyncStatus(status: 'pending' | 'synced' | 'failed'): Promise<TrappDB['carcasses']['value'][]> {
  const db = await getDB();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return db.getAllFromIndex('carcasses', 'by-sync-status', status as any);
}

export async function updateCarcassSyncStatus(id: string, status: 'pending' | 'synced' | 'failed'): Promise<void> {
  const db = await getDB();
  const carcass = await db.get('carcasses', id);
  if (carcass) {
    carcass.syncStatus = status;
    carcass.updatedAt = new Date().toISOString();
    await db.put('carcasses', carcass);
  }
}

/**
 * Reconcile a local carcass with the server's canonical record after a
 * successful sync (see reconcileSnare).
 */
export async function reconcileCarcass(
  localId: string,
  server: { id: string; carcassNumber?: string; photos?: PhotoUrlRef[] | null }
): Promise<void> {
  const db = await getDB();
  const existing = await db.get('carcasses', localId);
  if (!existing) return;

  const reconciled = {
    ...existing,
    id: server.id || existing.id,
    carcassNumber: server.carcassNumber || existing.carcassNumber,
    photoUrls: server.photos ?? existing.photoUrls,
    syncStatus: 'synced' as const,
    updatedAt: new Date().toISOString(),
  };

  const tx = db.transaction('carcasses', 'readwrite');
  if (reconciled.id !== localId) {
    await tx.store.delete(localId);
  }
  await tx.store.put(reconciled);
  await tx.done;
}

export async function deleteCarcass(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('carcasses', id);
}

// Sync queue operations
export async function addToSyncQueue(item: Omit<TrappDB['syncQueue']['value'], 'id' | 'createdAt' | 'retryCount'>): Promise<void> {
  const db = await getDB();
  const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  await db.put('syncQueue', {
    ...item,
    id,
    createdAt: new Date().toISOString(),
    retryCount: 0,
  });
}

export async function getSyncQueue(): Promise<TrappDB['syncQueue']['value'][]> {
  const db = await getDB();
  return db.getAll('syncQueue');
}

export async function removeSyncQueueItem(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('syncQueue', id);
}

export async function incrementSyncRetry(id: string, error: string): Promise<void> {
  const db = await getDB();
  const item = await db.get('syncQueue', id);
  if (item) {
    item.retryCount += 1;
    item.lastError = error;
    await db.put('syncQueue', item);
  }
}

// Settings operations
export async function setSetting(key: string, value: unknown): Promise<void> {
  const db = await getDB();
  await db.put('settings', {
    key,
    value,
    updatedAt: new Date().toISOString(),
  });
}

export async function getSetting<T>(key: string): Promise<T | undefined> {
  const db = await getDB();
  const setting = await db.get('settings', key);
  return setting?.value as T | undefined;
}

export async function deleteSetting(key: string): Promise<void> {
  const db = await getDB();
  await db.delete('settings', key);
}

// Clear all data (for testing/logout)
export async function clearAllData(): Promise<void> {
  const db = await getDB();
  await db.clear('snares');
  await db.clear('carcasses');
  await db.clear('syncQueue');
  await db.clear('settings');
}
